import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, createSelector, on } from '@ngrx/store';
import { AppState } from '../store';
import { EmployeeGroupChangeCompanyInformationDTO, EmployeeGroupChangeRequestDTO } from '@iota/inh-core-v1-management-client';
import {
    CancelEmployeeGroupChange,
    CancelEmployeeGroupChangeFailure,
    CancelEmployeeGroupChangeSuccess,
    LoadAllEmployeeGroupChangesByCompany,
    LoadAllEmployeeGroupChangesByCompanyFailure,
    LoadAllEmployeeGroupChangesByCompanySuccess,
    LoadCompanyEmployeeGroupChangeInformation,
    LoadCompanyEmployeeGroupChangeInformationFailure,
    LoadCompanyEmployeeGroupChangeInformationSuccess,
    ScheduleEmployeeGroupChange,
    ScheduleEmployeeGroupChangeFailure,
    ScheduleEmployeeGroupChangeSuccess,
    UpdateEmployeeGroupChangeDate,
    UpdateEmployeeGroupChangeDateFailure,
    UpdateEmployeeGroupChangeDateSuccess
} from '../actions/employee-group-change.actions';
import moment from 'moment';

export interface EmployeeGroupChangeState extends EntityState<EmployeeGroupChangeRequestDTO> {
    loading: boolean;
    actionLoading: boolean;
    actionError: any;
    error: any;
    companyInformation: Record<string, EmployeeGroupChangeCompanyInformationDTO>;
}

function selectId(a: EmployeeGroupChangeRequestDTO): string {
    return a.id;
}

function sortComparer(a: EmployeeGroupChangeRequestDTO, b: EmployeeGroupChangeRequestDTO): number {
    return moment(b.scheduledDate).toDate().getTime() - moment(a.scheduledDate).toDate().getTime();
}

export const employeeGroupChangeAdapter: EntityAdapter<EmployeeGroupChangeRequestDTO> = createEntityAdapter<EmployeeGroupChangeRequestDTO>({
    selectId,
    sortComparer
});

export const initialState: EmployeeGroupChangeState = employeeGroupChangeAdapter.getInitialState({
    loading: false,
    actionLoading: false,
    actionError: undefined,
    error: undefined,
    companyInformation: {}
});

export const employeeGroupChangeReducer = createReducer(
    initialState,
    on(
        LoadCompanyEmployeeGroupChangeInformation,
        ScheduleEmployeeGroupChange,
        UpdateEmployeeGroupChangeDate,
        CancelEmployeeGroupChange,
        (state) => ({ ...state, actionError: undefined, actionLoading: true })
    ),

    on(
        LoadCompanyEmployeeGroupChangeInformationFailure,
        ScheduleEmployeeGroupChangeFailure,
        UpdateEmployeeGroupChangeDateFailure,
        CancelEmployeeGroupChangeFailure,
        (state, { error }) => ({ ...state, actionError: error, actionLoading: false })
    ),

    on(LoadCompanyEmployeeGroupChangeInformationSuccess, (state, { companyId, companyInformation }) => ({
        ...state,
        companyInformation: { ...state.companyInformation, [companyId]: companyInformation },
        actionLoading: false
    })),

    on(LoadAllEmployeeGroupChangesByCompany, (state) => ({
        ...state,
        error: undefined,
        loading: true
    })),
    on(LoadAllEmployeeGroupChangesByCompanySuccess, (state, { employeeGroupChanges }) =>
        employeeGroupChangeAdapter.upsertMany(employeeGroupChanges || [], { ...state, loading: false })
    ),
    on(LoadAllEmployeeGroupChangesByCompanyFailure, (state, { error }) => ({ ...state, loading: false, error })),

    on(
        ScheduleEmployeeGroupChangeSuccess,
        UpdateEmployeeGroupChangeDateSuccess,
        CancelEmployeeGroupChangeSuccess,
        (state, { employeeGroupChangeRequest }) =>
            employeeGroupChangeAdapter.upsertOne(employeeGroupChangeRequest, { ...state, actionLoading: false })
    )
);

const { selectAll } = employeeGroupChangeAdapter.getSelectors();

export const employeeGroupChangeState = (state: AppState) => state.employeeGroupChangeState;
export const allEmployeeGroupChanges = createSelector(employeeGroupChangeState, selectAll);
export const employeeGroupChangeCompanyInformation = createSelector(employeeGroupChangeState, (state) => state.companyInformation);
export const employeeGroupChangesLoading = createSelector(employeeGroupChangeState, (state) => state.loading);
export const employeeGroupChangesActionLoading = createSelector(employeeGroupChangeState, (state) => state.actionLoading);
export const employeeGroupChangesActionError = createSelector(employeeGroupChangeState, (state) => state.actionError);

export const employeeGroupChangeRequestById = (id: string) =>
    createSelector(allEmployeeGroupChanges, (requests) => requests?.find((request) => request.id === id));
